import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import React, { useEffect, FC, useState, useCallback } from 'react';
import FroalaEditor from 'froala-editor';
import { AlertMessage, PromotionalContentSummary } from '../../../shared/types';
import { matchTagName } from '../../../utils/promotional-content.utils';
import Editor, { EditorProps } from './Editor';
import { makeCharts } from '../../../utils/tableau';

import { TableauChartsList } from './TableauChartsList';
import { useApolloClient } from '@apollo/react-hooks';

interface ArticleEditorProps extends EditorProps {
    promotionalContent?: PromotionalContentSummary[];
    setPageMessage?: (arg: AlertMessage) => void;
}

const ArticleEditor: FC<ArticleEditorProps> = ({
    initialHtml,
    onChange,
    errorMessage,
    promotionalContent,
    setPageMessage,
}) => {
    const [showDialog, setShowDialog] = useState(false);
    const [editor, setEditor] = useState<any>();
    const client = useApolloClient();

    const onChartSelected = useCallback(
        (chart: any) => {
            setShowDialog(false);
            editor.selection.restore();
            editor.html?.insert(
                `<p><div data-id="tableau-viz" data-name="${chart.path}">loading chart ...</div></p>`
            );
            makeCharts(client, editor.el);
        },
        [editor]
    );

    // Set promotional content icon
    useEffect(() => {
        //Define an icon template
        FroalaEditor.DefineIconTemplate(
            'custom_svg',
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">[PATH]</svg>'
        );
        // Define an icon
        FroalaEditor.DefineIcon('promotionalContentIcon', {
            PATH: '<path d="M18 0C16.8133 0 15.6533 0.351894 14.6666 1.01118C13.6799 1.67047 12.9108 2.60754 12.4567 3.7039C12.0026 4.80026 11.8838 6.00666 12.1153 7.17054C12.3468 8.33443 12.9182 9.40353 13.7574 10.2426C14.5965 11.0818 15.6656 11.6532 16.8295 11.8847C17.9933 12.1162 19.1997 11.9974 20.2961 11.5433C21.3925 11.0892 22.3295 10.3201 22.9888 9.33342C23.6481 8.34673 24 7.18669 24 6C24 4.4087 23.3679 2.88258 22.2426 1.75736C21.1174 0.632141 19.5913 0 18 0ZM21 7H19V9H17V7H15V5H17V3H19V5H21V7Z M18 14C16.6897 14.0012 15.3991 13.6802 14.242 13.0653C13.0849 12.4504 12.0967 11.5605 11.3645 10.4739C10.6322 9.38729 10.1783 8.13731 10.0427 6.83401C9.90713 5.53071 10.094 4.21407 10.587 3H1C0.734784 3 0.48043 3.10536 0.292893 3.29289C0.105357 3.48043 0 3.73478 0 4V23C0 23.2652 0.105357 23.5196 0.292893 23.7071C0.48043 23.8946 0.734784 24 1 24H20C20.2652 24 20.5196 23.8946 20.7071 23.7071C20.8946 23.5196 21 23.2652 21 23V13.413C20.0473 13.8006 19.0285 14 18 14ZM19 22H2V18H19V22Z" />',
            template: 'custom_svg',
        });
        FroalaEditor.DefineIconTemplate(
            'custom_svg_2',
            '<svg width="18" height="18" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg">[PATH]</svg>'
        );
        FroalaEditor.DefineIcon('tableauIcon', {
            PATH: '<path d="M512 896v512h-256v-512h256zm384-512v1024h-256v-1024h256zm1024 1152v128h-2048v-1536h128v1408h1920zm-640-896v768h-256v-768h256zm384-384v1152h-256v-1152h256z" />',
            template: 'custom_svg_2',
        });
        FroalaEditor.RegisterCommand('tableauCharts', {
            title: 'Add Tableau Chart',
            type: 'buttonIcon',
            icon: 'tableauIcon',
            undo: true,
            focus: true,
            callback: function () {
                (this as any).selection.save();
                setEditor(this);
                setShowDialog(true);
            },
        });
    }, []);

    // Set promotional content data
    useEffect(() => {
        if (promotionalContent?.length) {
            // Define a dropdown button
            FroalaEditor.RegisterCommand('promotionalContentDropdown', {
                // Button title.
                title: 'Promotional Content',

                // Mark the button as a dropdown.
                type: 'dropdown',

                // Specify the icon for the button.
                // If this option is not specified, the button name will be used.
                icon: 'promotionalContentIcon',

                // Options for the dropdown.
                options: { ...promotionalContent.map(({ name }) => name) },

                // Save the dropdown action into undo stack.
                undo: true,

                // Focus inside the editor before callback.
                focus: true,

                // Refresh the button state after the callback.
                refreshAfterCallback: true,

                // Callback.
                callback: function (_, val) {
                    let currentElement;
                    const currentPromotionalContent = promotionalContent[val];
                    try {
                        currentElement = this.html?.get().length
                            ? this.html?.getSelected()
                            : '';
                    } catch {
                        currentElement = '';
                    }
                    const bannerHtml = `<a ${currentPromotionalContent.link.length > 0 && 'target="_blank"'} data-promotional-name="${currentPromotionalContent.name}" data-promotional-id="${currentPromotionalContent._id}" class="promotional-content" href="${currentPromotionalContent.link.length > 0 ? currentPromotionalContent.link : `/demo/${currentPromotionalContent._id}`}">
											<img src="${currentPromotionalContent.articleImage}" alt="${currentPromotionalContent.name}" >
										</a>`;

                    // Insert banner
                    if (
                        matchTagName(currentElement) === 'p' &&
                        currentElement === '<p><br></p>'
                    ) {
                        this.html?.insert(`${bannerHtml}<p>`);
                    } else if (
                        matchTagName(currentElement) !== 'li' &&
                        matchTagName(currentElement) !== 'ol'
                    ) {
                        this.html?.insert(`<p>${bannerHtml}</p><p>`);
                    } else {
                        setPageMessage &&
                            setPageMessage({
                                style: 'info',
                                message:
                                    "You can't insert Promotional Content in a list.",
                            });
                    }
                },
            });
        }
    }, [promotionalContent, setPageMessage]);

    return (
        <>
            <Editor
                initialHtml={initialHtml}
                onChange={onChange}
                errorMessage={errorMessage}
            />
            <Dialog
                open={showDialog}
                onClose={() => setShowDialog(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Insert Tableau Chart</DialogTitle>
                <DialogContent>
                    <TableauChartsList onSelect={onChartSelected} />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ArticleEditor;
